/**
 * Project Configuration
 * Centralized project IDs and settings
 */

// Special project IDs - Only for projects that need special handling
export const PROJECT_IDS = {
  // Field Report Projects (require special handling)
  EQUIPMENT_YARD: 'f94d12c8-f951-45a5-b1c3-8419a37750fb',
  HOCON_SANDBOX: '7299ebe3-4055-47bb-b238-93503020d392',

  // Special constants
  COST_MODULE: 'cost-module'
}

/**
 * Projects that use Field Reports API (NOT Cost Management)
 * Only these projects need to be explicitly configured
 * All other projects automatically use Cost Management API
 */
export const FIELD_REPORT_PROJECTS = [
  PROJECT_IDS.EQUIPMENT_YARD,
  PROJECT_IDS.HOCON_SANDBOX
]

/**
 * Check if a project uses Cost Management workflow
 *
 * DYNAMIC BEHAVIOR:
 * - Field Report projects (Equipment Yard, Hocon Sandbox) → return false
 * - All other projects → return true (use Cost Management API)
 *
 * This makes the system work automatically for ANY new project!
 * No need to manually add project IDs anymore.
 */
export const isCostManagementProject = (projectId) => {
  // Explicitly exclude Equipment Yard and other field report projects
  if (FIELD_REPORT_PROJECTS.includes(projectId)) {
    return false
  }

  // For all other projects, use Cost Management API
  // This includes any new project automatically
  return true
}

/**
 * Template field definitions for Equipment Yard templates
 * Maps template IDs to their complete set of field IDs
 */
export const TEMPLATE_FIELD_CONFIGS = {
  // Service Station Fuel Log
  '904f2c31-f161-4f91-a20a-774e0df6d933': {
    name: 'Service Station Fuel Log',
    equipmentNumberFieldId: '6421984c-9b70-425e-837c-556d6483cde2',
    fields: [
      'a168234f-74c2-477c-95e3-f2a5e00f8694', // Date
      '275e5342-5d0d-4508-8712-82dffa8296ed', // Name
      'e2f5badc-d154-4c71-8ae2-1d8c678e3d7d', // Job Number
      '6421984c-9b70-425e-837c-556d6483cde2', // Equipment Number
      '55dbcda9-45db-4dfd-835b-f32e7e959368', // Fuel Type
      '2af55845-90f1-43e1-8b13-cbca3c68f2d9', // Quantity Issued
      '503a8287-6091-486e-8da4-7521c750b314', // Start Meter Reading
      '056556ec-0514-4e6b-9fdf-da4f234009e0', // End Meter Reading
      // Note: Approval tracking fields are hidden - they're updated automatically by the approval workflow
    ],
    // Define choice fields and their options
    choiceFields: {
      '55dbcda9-45db-4dfd-835b-f32e7e959368': [ // Fuel Type
        'D1 = Off Road',
        'D3 = On Road',
        'A3= Gas  '
      ]
    },
    // Job Number field ID - synced with location
    jobNumberFieldId: 'e2f5badc-d154-4c71-8ae2-1d8c678e3d7d'
  },
  // Fueling - Daily Fuel Truck (no Equipment Number field)
  'f5bba51b-9ae7-4176-8f6a-6a27341175bb': {
    name: 'Fueling - Daily Fuel Truck',
    equipmentNumberFieldId: null,
    fields: [
      // Tank Refill section
      'bc64a26a-4d96-401d-a324-0a4c65ebad64', // Highway Start (Tank Refill)
      '38a9cc27-1596-4b03-bfe6-9fb843c96896', // Highway End (Tank Refill)
      '29f2cb76-4809-4b9f-b8c3-0b16bd146e2c', // Highway Total (Tank Refill)
      '2819f9c2-a6a3-43f9-88e0-7449b2c43456', // Offroad Start (Tank Refill)
      'b845d79e-8903-4564-8cbb-8f6d94577703', // Offroad End (Tank Refill)
      '10df5abc-ea34-4233-a47d-b41bff9a5003', // Offroad Total (Tank Refill)
      // Fuel Truck Meters section
      '30f21309-4af7-4c99-bd9e-eb43796415ca', // Highway Start (Fuel Truck Meters)
      '779b06ce-b58b-40d1-9f6b-41ad7c853ecd', // Highway End (Fuel Truck Meters)
      'a7eb5420-197a-45be-95cd-5198e98e24f4', // Highway Total (Fuel Truck Meters)
      'c2c06336-6e50-488b-8831-b1721624e64b', // Offroad Start (Fuel Truck Meters)
      '483d27a6-cc1e-4081-9c23-0ff8e034791c', // Offroad End (Fuel Truck Meters)
      '3f6551d8-6624-4f96-8136-0e9af418c1ae', // Offroad Total (Fuel Truck Meters)
    ]
  },
  // Fueling - Gasoline
  '09ff490e-c3b2-4d71-876d-ca6eca14e333': {
    name: 'Fueling - Gasoline',
    equipmentNumberFieldId: 'ab82979f-a5d2-461c-8292-7d753dd6a7a1',
    fields: [
      'ab82979f-a5d2-461c-8292-7d753dd6a7a1', // Equipment Number
      'a4715c6c-c6b4-435a-ad66-210dc811c2f6', // Gasoline Gallons Issued
      'c78c74d0-1631-412c-a6b9-31f382314b28', // Equipment Meter Reading
      'b4f9dcee-bab8-4021-adc3-339c3083679a', // Remarks
    ]
  },
  // Fueling - Highway Diesel
  'c7ef1594-837f-4050-8448-ab697a4bd9d0': {
    name: 'Fueling - Highway Diesel',
    equipmentNumberFieldId: 'd9879716-f7ff-4264-a58c-3b6e45b5ee72',
    fields: [
      'd9879716-f7ff-4264-a58c-3b6e45b5ee72', // Equipment Number
      '7f41f3e7-bf42-42ba-aeca-e7d177f62cba', // Highway Gallons Issued
      '64f4a02f-e955-41ef-b5c6-8a08be78a434', // Gallons DEF Issued
      '6db5dd5a-2ce5-4796-b6b1-9efd81b87326', // Engine Hours
      'a48ba202-2bfb-4f46-af37-95c2eadadfb7', // Equipment Milage
      '944fdb7c-c5ca-4814-a911-416c4b7e6113', // Coolant Gallons
      '38057452-acc3-4f07-a00e-1b76d8a1a31e', // 15/40 Engine Oil quarts
      '9dba4427-ce17-485d-acd4-889c6072f2f3', // THF (Tractor Hydraulic Fluid) quarts
      'b4f8a90a-05f8-4df6-a81d-d7960f582605', // 80/90 Gear Oil Quarts
      'dfbd2654-1321-45ce-8677-fae0e7b2fb75', // Other Oil Quarts
      '057332ef-6d3e-45a0-958c-d255ab753615', // Remarks
    ],
    // Explicitly mark toggle fields (stored as boolVal in ACC but should display as Yes/No/NA)
    // These fields use Yes/No/NA three-state selection
    toggleFields: [
      'b4f8a90a-05f8-4df6-a81d-d7960f582605', // 80/90 Gear Oil Quarts (toggle)
      'dfbd2654-1321-45ce-8677-fae0e7b2fb75', // Other Oil Quarts (toggle)
    ]
  },
  // Fueling - Off Highway Diesel
  '9e76e0d1-410b-443e-a41d-16d684ed5f0a': {
    name: 'Fueling - Off Highway Diesel',
    equipmentNumberFieldId: 'd2ac6769-6f5b-4408-872d-54c2e5911752',
    fields: [
      'd2ac6769-6f5b-4408-872d-54c2e5911752', // Equipment Number
      '29579480-a67f-4728-aa1b-f3b24c87f1d0', // Off Highway Gallons Issued
      '203748b1-1745-49f5-b0fe-f99bcd79619d', // Gallons DEF Issued
      '5d2ceedf-bde4-4480-ba67-146681028062', // Equipment Meter Reading
      '47c34c8a-bdc2-40f8-be9f-9d45521f4eca', // Engine Oil (quarts)
      'dc0eb5b4-8d67-408b-a2bd-243e6ad09960', // THF (quarts)
      '0819fd4b-a3e8-4003-9bbe-74fa08f7229d', // 80/90 Oil (quarts)
      'c0a5a1ac-4706-4ca8-b361-e6d6e807465c', // Remarks
    ]
  }
}
